import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Check, X, ArrowRight } from 'lucide-react';

// Last updated date shown on the page AND mirrored into the
// ArticleStructuredData dateModified in LawFirmsPage.js. Keep the two in
// sync when you refresh this content.
export const LAW_FIRMS_LAST_UPDATED = '1 October 2026';

// Answer-engine-optimized question + answer blocks. Each answer is a
// self-contained 30 to 80 word paragraph that names Sortio in the first
// sentence so it stays quotable out of context. These questions are
// intentionally distinct from the LAW_FIRMS_FAQS accordion questions in
// LawFirmsFAQ.js so the two FAQPage schemas on this URL do not duplicate
// the same Question entries. No speed claims, accuracy numbers, "every
// file", or confidence scores: the preview and Apply are the safeguard.
const ANSWERS = [
  {
    question: 'How does Sortio file documents by client and matter?',
    answer:
      'You tell Sortio how your firm files, in plain English, and turn on Rename. With Analyze contents on, Sortio reads each document, even scans, finds the client and the matter, and proposes a folder and a new name for each file. The preview shows where each file goes and why. You fix any it got wrong, and nothing moves until you click Apply.',
  },
  {
    question: 'Does Sortio keep privileged client files confidential?',
    answer:
      "Sortio lets the firm choose what is sent. By default it uses OpenAI's cloud AI models through Sortio's API, which receive filenames and folder paths, plus text from each document, up to an excerpt of about 4 KB, when Analyze contents is on. OCR for scans runs on your computer. Firms that can't use a cloud AI can switch to Offline Mode (Ollama or LM Studio), which runs the model on their own computer. Firms that want their own provider contract can use their own API key (BYOK). The confidentiality brief covers the specifics.",
  },
  {
    question: 'Can Sortio read scanned documents?',
    answer:
      "Yes. With Analyze contents on, Sortio runs on-device OCR on scanned PDFs and photos of documents, on Mac and Windows (not on Linux). It reads the first pages of each scan, which usually carry the caption, the parties and the date. Scans it can't read are set aside in a _Needs Review folder instead of being placed by guess, so you can file them yourself.",
  },
  {
    question: 'Does Sortio work without Clio?',
    answer:
      'Yes. Sortio files documents into the folders on your computer, by client and matter, whether or not your firm uses Clio. You can point it at your existing client folders so it files into them instead of inventing new ones. The Clio integration, included with Pro, is an extra step for firms that keep documents in Clio Manage.',
  },
  {
    question: 'How does Sortio match a file to a Clio matter?',
    answer:
      "Sortio pulls your open matters through the official Clio Manage API and compares each file's name, and the names and details it found inside the document, with that list. It suggests a matter for each file and leaves the ones it isn't confident about unselected. You review the plan, and only the files you approve are uploaded to the matter's documents in Clio.",
  },
  {
    question: 'How much does Sortio cost for a solo attorney or small firm?',
    answer:
      'Sortio is free to try: the free trial includes a one-time AI allowance, enough to run it on a real folder, and no credit card is required. Pro is $14.99 per month or $99 per year and adds a monthly AI allowance sized for heavy use, the Clio integration, and watch folders. Team is $29 per seat per month for shared rules and an admin console.',
  },
];

// Firm-framed comparison. Three columns let a firm see why neither manual
// filing nor a rule-only automation tool (the Hazel style approach) covers
// matter-based legal filing the way Sortio does. Kept as a real table so it
// is extractable by answer engines.
const COMPARISON_ROWS = [
  {
    dimension: 'Who reads each document',
    manual: 'A person opens and reads every file',
    rules: 'Nobody. Rules match the filenames or keywords you wrote',
    sortio: 'It reads each document, even scans, and you check the plan',
  },
  {
    dimension: 'Finding the client and matter',
    manual: 'A person reads each file and decides',
    rules: 'Only where a rule already covers the pattern',
    sortio: 'Found in the document and matched to your client and matter folders',
  },
  {
    dimension: 'Catching a misfile',
    manual: 'Often found weeks later, during discovery prep',
    rules: 'A wrong rule files silently and repeatedly until caught',
    sortio: 'The preview shows where each file goes and why, before anything moves',
  },
  {
    dimension: 'Naming',
    manual: 'Varies by who did the filing',
    rules: 'Consistent only where a rule already exists',
    sortio: 'Renamed to the convention you describe, and you review each name',
  },
  {
    dimension: 'Record of what moved',
    manual: 'None, or an ad hoc spreadsheet',
    rules: 'Limited to what the automation tool logs',
    sortio: 'Each applied sort is saved in Activity, where you can undo it',
  },
];

const AnswerBlock = ({ question, answer }) => (
  <div className="bg-apple-800/40 backdrop-blur-sm rounded-2xl border border-apple-700/50 p-6">
    <h2 className="text-xl sm:text-2xl font-semibold mb-3 tracking-tight text-apple-50">
      {question}
    </h2>
    <p className="text-apple-200 leading-relaxed">{answer}</p>
  </div>
);

const LawFirmsAnswers = () => {
  // FAQPage schema scoped to the six answer blocks above. This is a
  // separate, non-overlapping set from the LawFirmsFAQ accordion schema, so
  // the two scripts on this URL never repeat a Question.
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: ANSWERS.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <section id="law-firms-answers" className="my-16 sm:my-20">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="text-center mb-6">
        <h2 className="text-3xl sm:text-4xl font-semibold mb-4 tracking-tight text-apple-50">
          AI document filing for law firms, answered
        </h2>
        <p className="text-apple-300 text-lg font-light max-w-2xl mx-auto">
          Straight answers to what solo attorneys and small firms ask before they put Sortio on a
          real intake folder.
        </p>
        <p className="text-sm text-apple-400 mt-4">
          <em>Last updated: {LAW_FIRMS_LAST_UPDATED}</em>
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
        {ANSWERS.map((item, i) => (
          <AnswerBlock key={i} question={item.question} answer={item.answer} />
        ))}
      </div>

      {/* Firm vs manual vs rule-only comparison */}
      <div className="mt-16 max-w-5xl mx-auto">
        <h3 className="text-2xl sm:text-3xl font-semibold mb-3 tracking-tight text-apple-50 text-center">
          Sortio vs manual filing vs rule-only automation
        </h3>
        <p className="text-apple-300 text-base font-light max-w-3xl mx-auto text-center mb-8">
          How matter-based filing in Sortio compares with filing documents by hand and with a
          Hazel style rule-only tool that has no understanding of your matters.
        </p>

        {/* Phones: one card per row, Sortio first. A 4-column table pushes the
            Sortio column off-screen at 390px. */}
        <ul className="md:hidden space-y-4 list-none">
          {COMPARISON_ROWS.map((row, i) => (
            <li key={i} className="bg-apple-800/40 rounded-2xl border border-apple-700/50 p-5">
              <h4 className="text-base font-medium text-apple-50 mb-3">{row.dimension}</h4>
              <p className="flex items-start gap-2 text-sm text-apple-100 mb-3">
                <Check className="w-4 h-4 text-sortio-primary mt-0.5 flex-shrink-0" aria-hidden="true" />
                <span><span className="font-medium text-sortio-primary">Sortio: </span>{row.sortio}</span>
              </p>
              <p className="flex items-start gap-2 text-sm text-apple-300 mb-2">
                <X className="w-4 h-4 text-apple-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <span><span className="font-medium text-apple-200">Manual: </span>{row.manual}</span>
              </p>
              <p className="flex items-start gap-2 text-sm text-apple-300">
                <X className="w-4 h-4 text-apple-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <span><span className="font-medium text-apple-200">Rule-only tool: </span>{row.rules}</span>
              </p>
            </li>
          ))}
        </ul>

        <div className="hidden md:block overflow-x-auto rounded-2xl border border-apple-700/50">
          <table className="w-full text-left border-collapse">
            <caption className="sr-only">
              Comparison of manual filing, rule-only automation, and Sortio for law firm document
              filing.
            </caption>
            <thead>
              <tr className="bg-apple-800/60">
                <th scope="col" className="p-4 text-sm font-semibold text-apple-300">
                  &nbsp;
                </th>
                <th scope="col" className="p-4 text-sm font-semibold text-apple-200">
                  Manual filing
                </th>
                <th scope="col" className="p-4 text-sm font-semibold text-apple-200">
                  Rule-only tool
                </th>
                <th scope="col" className="p-4 text-sm font-semibold text-sortio-primary">
                  Sortio
                </th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON_ROWS.map((row, i) => (
                <tr
                  key={i}
                  className={i % 2 === 0 ? 'bg-apple-800/20' : 'bg-apple-800/40'}
                >
                  <th
                    scope="row"
                    className="p-4 text-sm font-medium text-apple-100 align-top"
                  >
                    {row.dimension}
                  </th>
                  <td className="p-4 text-sm text-apple-300 align-top">
                    <span className="flex items-start gap-2">
                      <X className="w-4 h-4 text-apple-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
                      {row.manual}
                    </span>
                  </td>
                  <td className="p-4 text-sm text-apple-300 align-top">
                    <span className="flex items-start gap-2">
                      <X className="w-4 h-4 text-apple-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
                      {row.rules}
                    </span>
                  </td>
                  <td className="p-4 text-sm text-apple-100 align-top">
                    <span className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-sortio-primary mt-0.5 flex-shrink-0" aria-hidden="true" />
                      {row.sortio}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Link
            to="/download"
            className="apple-button text-base px-8 py-4 flex items-center justify-center"
          >
            Try it free on a folder <ArrowRight className="ml-2" size={18} />
          </Link>
          <Link
            to="/checkout/team"
            className="text-apple-50 hover:text-sortio-primary transition-all px-6 py-3 flex items-center justify-center border border-apple-300/30 rounded-full hover:border-sortio-primary"
          >
            Get Sortio Team
          </Link>
        </div>
      </div>
    </section>
  );
};

export default LawFirmsAnswers;
